import { type MouseEvent } from 'react';
import { clsx } from 'clsx';
import {
  FavoriteHeartButton,
  FavoriteHeartIcon,
  type ManageCookbookItem,
  useShowFavoriteHeart,
  useFavoriteToast,
  useUnsaveOpensManage,
} from '@hungryroot/pho';
import { useNextOrderV3 } from '@hungryroot/rest-hooks';
import {
  type AccessedFrom,
  type FeedInteractionFields,
  type Location,
} from '@hungryroot/segment';
import { TransparentButton, Bookmark } from '@hungryroot/ui-kit';
import { type IconButtonProps } from '@hungryroot/ui-kit';
import { useDeleteFavoriteProduct } from '../../hooks/useDeleteFavoriteProduct';
import { useIsFavoriteProduct } from '../../hooks/useGetFavoriteProducts';
import { usePostFavoriteProduct } from '../../hooks/usePostFavoriteProduct';
import { FavoriteGroceriesIcon } from './FavoriteGroceriesIcon';
import styles from './FavoriteGroceries.module.css';

type VariantProps =
  | {
      variant: 'PDP';
      iconButtonVariant?: Pick<
        IconButtonProps,
        'prominence' | 'style' | 'size'
      >;
    }
  | { variant?: 'card' };

type FavoriteGroceriesProps = {
  testid?: string;
  customerId: number;
  productId: number;
  productName: string;
  className?: string;
  /**
   * Columns for the `Product Interaction` (saved/unsaved) event. Spreads
   * {@link FeedInteractionFields} so a call site inside a marketing carousel can
   * supply `container` / `carousel_id` / `carousel_name` /
   * `horizontal_position` per the Exp 242 nav-IA logging spec.
   */
  segment: FeedInteractionFields & {
    location: Location | null;
    pageName?: string;
    accessedFrom?: AccessedFrom;
    conversationId?: string;
    searchId?: string;
    resultPosition?: number;
  };
  onFavorite?: () => void;
  onFavoriteSuccess?: () => void;
  /**
   * Always render the heart, bypassing the Exp 242 (`useShowFavoriteHeart`)
   * gate. Set only by freemium-only surfaces (Exp 255) that mount the heart
   * regardless of 242 — e.g. the guest cookbook modal footer. Off by default so
   * every other consumer keeps following the 242 gate.
   */
  forceHeart?: boolean;
} & VariantProps;

export function FavoriteGroceries(props: FavoriteGroceriesProps) {
  const {
    testid,
    customerId,
    productId,
    productName,
    segment,
    onFavorite,
    onFavoriteSuccess,
    className,
    forceHeart = false,
  } = props;

  const { isFavorited, isFetching } = useIsFavoriteProduct(
    customerId,
    productId
  );

  const postFavoriteProduct = usePostFavoriteProduct();
  const deleteFavoriteProduct = useDeleteFavoriteProduct();

  const { isPending: isPendingPostFavoriteProduct } = postFavoriteProduct;
  const { isPending: isPendingDeleteFavoriteProduct } = deleteFavoriteProduct;

  const { order } = useNextOrderV3();

  const heart = useShowFavoriteHeart();
  const notifyFavoriteToast = useFavoriteToast();
  const openManageInsteadOfUnsaving = useUnsaveOpensManage();

  if (isFavorited === undefined) {
    return null;
  }

  // One item for both the toast and the Manage predicate, so the sheet a tap
  // opens and the sheet the toast's Manage opens can never describe different
  // things. No image here — the sheet resolves artwork from the id.
  const manageItem: ManageCookbookItem = {
    itemType: 'product',
    itemId: productId,
    title: productName,
  };

  const isDisabled =
    isFetching ||
    isPendingPostFavoriteProduct ||
    isPendingDeleteFavoriteProduct;

  const runToggle = (shouldFavorite: boolean, notify = true) => {
    const mutation = {
      data: {
        customerId,
        productId,
        productName,
        segment: { ...segment, orderId: order?.id },
      },
    };

    if (shouldFavorite) {
      onFavorite?.();
      postFavoriteProduct.mutate(mutation, {
        onSuccess: () => {
          onFavoriteSuccess?.();
        },
      });
    } else {
      deleteFavoriteProduct.mutate(mutation);
    }

    if (notify) {
      notifyFavoriteToast({
        isFavorited: shouldFavorite,
        onUndo: () => runToggle(!shouldFavorite, false),
        item: manageItem,
      });
    }
  };

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    // A filled heart opens Manage rather than unsaving on the spot — the unsave
    // happens in there, where it can show what it will cost. Same rule as the
    // home-feed heart, so a grocery behaves the same wherever it is hearted.
    if (openManageInsteadOfUnsaving(isFavorited, manageItem)) {
      return;
    }

    runToggle(!isFavorited);
  };

  const classNames = clsx(
    {
      [styles.inactive]: !isFavorited,
      [styles.selected]: isFavorited,
      [styles.disabled]: isDisabled,
    },
    className
  );

  const ariaLabel = `${isFavorited ? 'Remove' : 'Add'} ${productName} ${
    isFavorited ? 'from' : 'to'
  } favorites`;

  const iconProps = {
    ariaLabel,
    className: classNames,
    testid,
    onClick: handleClick,
    disabled: isDisabled,
  };

  if (!forceHeart && !heart.isReady) {
    return null;
  }

  if (forceHeart || (heart.isReady && heart.showHeart)) {
    // Heart manages its own fill, so drop the bookmark color classes and keep
    // only the caller-supplied positioning className.
    if (props.variant === 'PDP') {
      return (
        <FavoriteHeartButton
          isFavorited={isFavorited}
          onClick={handleClick}
          ariaLabel={ariaLabel}
          size={props.iconButtonVariant?.size ?? 'small'}
          disabled={isDisabled}
          className={className}
          testid={testid}
        />
      );
    }

    return (
      <TransparentButton
        ariaLabel={ariaLabel}
        // styles.container sizes the heart to 24px so it matches the pairing
        // (SaveRecipes) heart; the heart manages its own fill, so the
        // inactive/selected color classes are intentionally omitted.
        className={clsx(styles.container, className)}
        testid={testid}
        onClick={handleClick}
        disabled={isDisabled}
      >
        <FavoriteHeartIcon isFavorited={isFavorited} />
      </TransparentButton>
    );
  }

  if (props.variant === 'PDP') {
    return (
      <FavoriteGroceriesIcon variant={props.iconButtonVariant} {...iconProps} />
    );
  }

  return (
    <TransparentButton {...iconProps}>
      <Bookmark />
    </TransparentButton>
  );
}
